import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useToggleDetailOverlay from '../../../hooks/useToggleDetailOverlay';
import { useDispatch, useSelector } from 'react-redux';
import h, { getCountry } from '../../../helpers/common';
import { useContext, useEffect } from 'react';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { EVENT } from '../../../contexts/MixpanelConstants';
import mpx from '../../../actions/mpx_data';
import {
    open as openCastCrewModal,
    close as closeCastCrewModal,
    updateContent as updateCastCrewModalContent,
} from '../../../store/reducers/castCrewModalSlice';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { container } from '@intigral/domain/lib/DataDomain';
import { transformFilmography } from '../../../utils/transformResponse/mpxResponses';
import { DI } from '@intigral/constant/config/DIContainer';

export const usePersons = (id, contentId) => {
    // state
    const castCrewModal = useSelector((state: any) => state.castCrewModal);
    const isModalOpened = castCrewModal?.isOpen;
    const modalContent = castCrewModal?.modalContent;

    // hooks
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { showDetailOverlay, removeSearchParams } = useToggleDetailOverlay();
    const { track }: { track: any } = useContext(MixpanelContext);

    // react queries
    const { isLoading, data, isError, error } = useQuery({
        queryKey: ['overlay', 'cast-crew'],
        enabled: true,
        queryFn: async () => {
            const castCrewList = await mpx.getCastCrew(contentId);
            return { castCrewList };
        },
        refetchOnWindowFocus: false,
    });

    const openDetailOverlay = (item) => {
        dispatch(closeCastCrewModal());
        removeSearchParams(['movie', 'series', 'guid', 'season']);
        const itemId = item.Id;
        const typeKey = item.programType === 'movie' ? 'movie' : 'series';
        showDetailOverlay({
            [typeKey]: btoa(itemId),
            guid: btoa(item?.guid),
            season: btoa(item?.tvSeasonNumber || item?.seasonNumber || null),
        });
    };

    // useEffects
    useEffect(() => {
        if (contentId) {
            queryClient.invalidateQueries({ queryKey: ['overlay', 'cast-crew'] });
        }
    }, [contentId]);

    useEffect(() => {
        if (isModalOpened) {
            window.history.pushState({ modal: true }, '');
        }
    }, [isModalOpened]);

    useEffect(() => {
        const handlePopState = () => {
            if (isModalOpened) {
                dispatch({ type: 'castCrewModal/close' });
                window.history.pushState({ modal: false }, '');
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isModalOpened, dispatch]);

    const filmographyMutation = useMutation({
        mutationFn: async (biography: any) => {
            const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
            const filmographyResponse = await mpxService.getFilmography(`{PC}{${getCountry()}}`, biography.id);
            const filmography = transformFilmography(filmographyResponse.entries);
            return { filmography, biography };
        },
        onSuccess: (data) => {
            dispatch(
                updateCastCrewModalContent({
                    ...modalContent,
                    filmography: data.filmography,
                })
            );
        },
    });

    const modalSettings = {
        isModalOpened,
        closeModal() {
            dispatch(closeCastCrewModal());
        },
        async openModal(modalContentArg) {
            dispatch(
                openCastCrewModal({
                    position: modalContentArg?.position,
                    title: h.isEnglishLang() ? modalContentArg.title : modalContentArg.titleAr,
                    image: modalContentArg.image,
                })
            );
            filmographyMutation.mutate(modalContentArg);
            track(EVENT.CAST_CREW.VOD_CAST_CREW_VIEW, {
                CastName: modalContentArg?.title,
                CastRole: Array.isArray(modalContentArg?.position)
                    ? modalContentArg?.position?.join(',')
                    : modalContentArg?.position,
            });
        },
        modalContent,
    };

    return {
        isLoading,
        data,
        modalSettings,
        isModalOpened,
        modalContent,
        openDetailOverlay,
    };
};
